import React, { useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FaArrowLeft, FaEdit, FaExternalLinkAlt, FaTrash } from "react-icons/fa";
import toast from "react-hot-toast";
import adminApi from "services/admin-api";
import { buildCurrentUserQueryOptions } from "queries/current-user";
import RightDrawer from "components/global/RightDrawer";
import { NewsArticleForm } from "pages/app/news/news-article-form";
import { formatDateTime, getApiErrorMessage } from "utils/admin";
import { AppLayout } from "components/layouts/app-layout/app-layout";
import { DataTable } from "components/molecules/table/table";
import { PageHeader, SectionCard } from "components/admin/ui";
import {
  NewsDetailActionLink,
  NewsDetailSecondaryButton,
  NewsDetailGridLayout,
  NewsDetailContentStack,
  NewsDetailGridLayoutSecondary,
  NewsDetailSection,
  NewsDetailHeroImage,
  NewsDetailBodyText,
  NewsDetailEyebrowText,
  NewsDetailTitleText,
} from "pages/app/news/styles/news-detail.page.inline.styles";
const NewsDetailPage = () => {
  const { id } = useParams<{
    id: string;
  }>();
  const history = useHistory();
  const queryClient = useQueryClient();
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const { data: article } = useQuery({
    queryKey: ["news-article", id],
    queryFn: () => adminApi.news.get(id),
    enabled: Boolean(id),
  });
  const { data: currentUser } = useQuery(buildCurrentUserQueryOptions());
  const { data: sports = [] } = useQuery({
    queryKey: ["sports"],
    queryFn: () => adminApi.sports.list(),
    enabled: currentUser?.role === "admin",
  });
  const { data: teams = [] } = useQuery({
    queryKey: ["teams"],
    queryFn: () => adminApi.teams.list(),
    enabled: currentUser?.role === "admin",
  });
  const { data: athletes = [] } = useQuery({
    queryKey: ["athletes"],
    queryFn: () => adminApi.athletes.list(),
    enabled: currentUser?.role === "admin",
  });
  const canEditSource = currentUser?.role === "admin";
  const updateMutation = useMutation({
    mutationFn: (payload: Record<string, any>) => adminApi.news.update(id, payload),
    onSuccess: (updatedArticle: any) => {
      queryClient.setQueryData(["news-article", id], updatedArticle);
      queryClient.invalidateQueries({
        queryKey: ["news-feed"],
      });
      queryClient.invalidateQueries({
        queryKey: ["dashboard-news"],
      });
      queryClient.invalidateQueries({
        queryKey: ["dashboard-fan-news"],
      });
      toast.success("News article updated.");
      setDrawerOpen(false);
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to update this news article."));
    },
  });
  const deleteMutation = useMutation({
    mutationFn: () => adminApi.news.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["news-feed"],
      });
      queryClient.invalidateQueries({
        queryKey: ["dashboard-news"],
      });
      queryClient.invalidateQueries({
        queryKey: ["dashboard-fan-news"],
      });
      toast.success("News article removed.");
      history.push("/news");
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to remove this news article."));
    },
  });
  return (
    <AppLayout route="/news">
      <PageHeader
        title={article?.title || "News detail"}
        description="Inspect the full story body and the related content associations returned by the news API."
        crumbs={[
          {
            label: "Dashboard",
            to: "/dashboard",
          },
          {
            label: "News",
            to: "/news",
          },
          {
            label: article?.title || "Article",
          },
        ]}
        actions={
          <>
            <NewsDetailSecondaryButton onClick={() => history.push("/news")} type="button">
              <FaArrowLeft size={12} />
              Back
            </NewsDetailSecondaryButton>
            {canEditSource ? (
              <NewsDetailSecondaryButton onClick={() => setDrawerOpen(true)} type="button">
                <FaEdit size={12} />
                Edit article
              </NewsDetailSecondaryButton>
            ) : null}
            {canEditSource ? (
              <NewsDetailSecondaryButton
                onClick={() => {
                  if (window.confirm("Delete this news article? This action cannot be undone.")) {
                    deleteMutation.mutate();
                  }
                }}
                type="button"
              >
                <FaTrash size={12} />
                Delete
              </NewsDetailSecondaryButton>
            ) : null}
            {article?.external_url ? (
              <NewsDetailActionLink href={article.external_url} rel="noreferrer" target="_blank">
                <FaExternalLinkAlt size={12} />
                Open source
              </NewsDetailActionLink>
            ) : null}
          </>
        }
      />

      <NewsDetailGridLayout>
        <SectionCard subtitle="Primary story content" title="Article">
          <NewsDetailContentStack>
            <NewsDetailHeroImage
              alt={article?.title || "News story"}
              fallbackLabel={article?.title}
              src={article?.image_url}
              variant="news"
            />
            <NewsDetailGridLayoutSecondary>
              <NewsDetailSection>
                <NewsDetailEyebrowText>Source</NewsDetailEyebrowText>
                <NewsDetailTitleText>{article?.source_name || "Unknown"}</NewsDetailTitleText>
              </NewsDetailSection>
              <NewsDetailSection>
                <NewsDetailEyebrowText>Published</NewsDetailEyebrowText>
                <NewsDetailTitleText>{formatDateTime(article?.published_at)}</NewsDetailTitleText>
              </NewsDetailSection>
              <NewsDetailSection>
                <NewsDetailEyebrowText>Sport</NewsDetailEyebrowText>
                <NewsDetailTitleText>{article?.sport_name || "General"}</NewsDetailTitleText>
              </NewsDetailSection>
              <NewsDetailSection>
                <NewsDetailEyebrowText>Athlete</NewsDetailEyebrowText>
                <NewsDetailTitleText>{article?.player_name || "Not linked"}</NewsDetailTitleText>
              </NewsDetailSection>
            </NewsDetailGridLayoutSecondary>
            <NewsDetailSection>
              <NewsDetailEyebrowText>Content</NewsDetailEyebrowText>
              <NewsDetailBodyText>
                {article?.content ||
                  article?.excerpt ||
                  "No article body is available for this story yet."}
              </NewsDetailBodyText>
            </NewsDetailSection>
          </NewsDetailContentStack>
        </SectionCard>

        <SectionCard subtitle="Server-provided related content" title="Related Articles">
          <DataTable
            canEdit={false}
            canRemove={false}
            columns={[
              {
                label: "Title",
                accessor: (row: any) => row.title,
              },
              {
                label: "Source",
                accessor: (row: any) => row.source_name || "Unknown",
              },
              {
                label: "Sport",
                accessor: (row: any) => row.sport_name || "General",
              },
              {
                label: "Published",
                accessor: (row: any) => formatDateTime(row.published_at),
              },
            ]}
            data={article?.related_articles || []}
            hideNav
            onView={(relatedId) => history.push(`/news/${relatedId}`)}
          />
        </SectionCard>
      </NewsDetailGridLayout>

      <RightDrawer isOpen={isDrawerOpen} onClose={() => setDrawerOpen(false)} title="Edit article">
        <NewsArticleForm
          initialValues={{
            title: article?.title || "",
            excerpt: article?.excerpt || "",
            content: article?.content || "",
            image_url: article?.image_url || "",
            published_at: article?.published_at || "",
            author: article?.author || "",
            source_name: article?.source_name || "",
            source_url: article?.source_url || "",
            external_url: article?.external_url || "",
            sport_id: article?.sport_id ? String(article.sport_id) : "",
            team_id: article?.team_id ? String(article.team_id) : "",
            player_id: article?.player_id ? String(article.player_id) : "",
          }}
          isSubmitting={updateMutation.isPending}
          onCancel={() => setDrawerOpen(false)}
          onSubmit={(values) => updateMutation.mutate(values)}
          playerOptions={athletes.map((athlete: any) => ({
            label: athlete.full_name,
            value: athlete.id,
          }))}
          sportOptions={sports.map((sport: any) => ({
            label: sport.name,
            value: sport.id,
          }))}
          submitLabel="Save changes"
          teamOptions={teams.map((team: any) => ({
            label: team.name,
            value: team.id,
          }))}
        />
      </RightDrawer>
    </AppLayout>
  );
};
export default NewsDetailPage;
